import { del, get, post } from '@api/api-client';
import {
  appendPaginationQuery,
  type PaginatedResponse,
  type PaginationParams,
} from '@api/pagination';

export interface VoteResponse {
  id: string;
  nominationId: string;
  userId: string;
  createdAt: string;
}

export interface VoteDetailParticipantResponse {
  id: string;
  name: string;
  surname: string | null;
}

export interface VoteDetailNominationResponse {
  id: string;
  participant: VoteDetailParticipantResponse;
}

export interface VoteDetailUserResponse {
  id: string;
  email: string;
}

export interface VoteDetailResponse {
  id: string;
  createdAt: string;
  user: VoteDetailUserResponse;
  nomination: VoteDetailNominationResponse;
}

export interface VoteEditionDetailCategoryResponse {
  id: string;
  name: string;
}

export interface VoteEditionDetailResponse {
  id: string;
  createdAt: string;
  user: VoteDetailUserResponse;
  nomination: VoteDetailNominationResponse;
  category: VoteEditionDetailCategoryResponse;
}

export interface VoteEditionVoterExportResponse {
  docplannerId: string | null;
  email: string;
}

export interface VoteEditionNomineeVotesExportResponse {
  categoryExternalId: number | null;
  dpDoctorId: number;
  name: string;
  surname: string | null;
  email: string | null;
  categoryName: string;
  isClinic: boolean;
  voteCount: number;
}

export interface CreateVoteDTO {
  nominationId: string;
  userId: string;
}

export interface VoteUserDetailParticipantResponse {
  id: string;
  name: string;
  surname: string | null;
}

export interface VoteUserDetailNominationResponse {
  id: string;
  participant: VoteUserDetailParticipantResponse;
}

export interface VoteUserDetailCategoryResponse {
  id: string;
  name: string;
}

export interface VoteUserDetailEditionResponse {
  id: string;
  name: string;
  year: number;
}

export interface VoteUserDetailResponse {
  id: string;
  createdAt: string;
  nomination: VoteUserDetailNominationResponse;
  category: VoteUserDetailCategoryResponse;
  edition: VoteUserDetailEditionResponse;
}

/** Fetches all votes with pagination. */
export function getVotes(params?: PaginationParams): Promise<PaginatedResponse<VoteResponse>> {
  return get<PaginatedResponse<VoteResponse>>(appendPaginationQuery('/votes', params));
}

/** Fetches detailed votes for a given nomination with pagination (includes user data). */
export function getVotesByNomination(
  nominationId: string,
  params?: PaginationParams,
): Promise<PaginatedResponse<VoteDetailResponse>> {
  return get<PaginatedResponse<VoteDetailResponse>>(
    appendPaginationQuery(`/nominations/${nominationId}/votes`, params),
  );
}

/** Fetches detailed votes for a given edition with pagination. */
export function getVotesByEdition(
  editionId: string,
  params?: PaginationParams,
): Promise<PaginatedResponse<VoteEditionDetailResponse>> {
  return get<PaginatedResponse<VoteEditionDetailResponse>>(
    appendPaginationQuery(`/editions/${editionId}/votes`, params),
  );
}

/** Fetches unique edition voters for CSV export. */
export function getEditionVoterExport(
  editionId: string,
): Promise<VoteEditionVoterExportResponse[]> {
  return get<VoteEditionVoterExportResponse[]>(`/editions/${editionId}/voters-export`);
}

/** Fetches per-nominee vote counts for an edition for CSV export. */
export function getEditionNomineesVotesExport(
  editionId: string,
): Promise<VoteEditionNomineeVotesExportResponse[]> {
  return get<VoteEditionNomineeVotesExportResponse[]>(
    `/editions/${editionId}/nominees-votes-export`,
  );
}

/** Fetches detailed votes for a given edition and category with pagination. */
export function getVotesByEditionAndCategory(
  editionId: string,
  categoryId: string,
  params?: PaginationParams,
): Promise<PaginatedResponse<VoteDetailResponse>> {
  return get<PaginatedResponse<VoteDetailResponse>>(
    appendPaginationQuery(`/editions/${editionId}/categories/${categoryId}/votes`, params),
  );
}

/** Fetches detailed votes for a given user with pagination (includes edition, category, and nominee). */
export function getVotesByUser(
  userId: string,
  params?: PaginationParams,
  editionId?: string,
): Promise<PaginatedResponse<VoteUserDetailResponse>> {
  let path = appendPaginationQuery(`/users/${userId}/votes`, params);
  if (editionId) {
    const separator = path.includes('?') ? '&' : '?';
    path += `${separator}editionId=${encodeURIComponent(editionId)}`;
  }
  return get<PaginatedResponse<VoteUserDetailResponse>>(path);
}

/** Fetches the list of editions a user has voted in (sorted by year descending). */
export function getUserVoteEditions(userId: string): Promise<VoteUserDetailEditionResponse[]> {
  return get<VoteUserDetailEditionResponse[]>(`/users/${userId}/vote-editions`);
}

/** Casts a new vote. */
export function createVote(data: CreateVoteDTO): Promise<VoteResponse> {
  return post<VoteResponse>('/votes', data);
}

/** Deletes a vote by ID. */
export function deleteVote(id: string): Promise<void> {
  return del(`/votes/${id}`);
}
